import { message } from 'antd'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'

import {
  addFloor,
  addPosOjol,
  createOutlet,
  createProductFavorites,
  deleteFloor,
  deleteOutlet,
  deletePosOjol,
  deleteProductFavorites,
  editFloor,
  editOutlet,
  editPosOjol,
  editPosRole,
  getOutlets,
  getPosOjols,
  getPosOrder,
  getPosOrders,
  getPosOrderSummary,
  getPosOutletDetail,
  getPosRoles,
  getPosSalesByPaymentType,
  getPosSalesByPaymentTypeDetail,
  getPosSalesByInvoiceType,
  getPosSalesByInvoiceTypes,
  getPosShiftDetail,
  getPosShifts,
  getPosUsers,
  getProductFavorites,
  getSuggestionOjols,
  getSuggestionOutlet,
  moveProductFavorite,
  outletFloors,
  resetOrderTablesByRoom,
  resetOrderTablesByTable,
  getPosSalesDetailSummary,
  getPosSalesDetail,
  getPosSalesPriceRule,
  getPosSalesPriceRuleTransactions,
  createQRCodePayment,
  createPaymentGateway,
  createPosInvoice,
  payPosOrder,
  changePin,
  verifyPin,
  getPosCashierWidget,
  addPosReturn,
  getPosCashierCashInOut,
  deleteFinanceBankTransWithReason,
} from 'utils/apis'
import { sendEmailOrderBill } from '../apis'

export const useProductFavorites = ({ options }) => {
  return useQuery(
    ['products-favorites'],
    async () => {
      const {
        data: { data },
      } = await getProductFavorites()
      return data
    },
    { ...options },
  )
}

export const useCreateProductFavorites = () => {
  return useMutation((payload) => createProductFavorites(payload))
}

export const useDeleteProductFavorites = () => {
  return useMutation(({ id }) => deleteProductFavorites({ id }))
}

export const useMoveProductFavorite = () => {
  return useMutation(({ id, payload }) => moveProductFavorite({ id, payload }))
}

export const useOutlets = ({ options, payload }) => {
  return useQuery(
    ['outlets', payload],
    async () => {
      const {
        data: { data },
      } = await getOutlets({ payload })
      return data
    },
    { ...options },
  )
}

export const useCreateOutlet = () => {
  return useMutation(({ payload }) => createOutlet({ payload }))
}

export const useEditOutlet = () => {
  return useMutation(({ id, payload }) => editOutlet({ id, payload }))
}

export const useDeleteOutlet = () => {
  return useMutation(({ id, payload }) => deleteOutlet({ id, payload }))
}

export const usePosUsers = ({ options = {}, payload } = {}) => {
  return useQuery(
    ['pos-users', payload],
    async () => {
      const {
        data: { data },
      } = await getPosUsers({ payload })
      return data
    },
    { ...options },
  )
}

export const useOutletFloors = ({ options, payload }) => {
  return useQuery(
    ['outletFloors', payload],
    async () => {
      const {
        data: { data },
      } = await outletFloors({ payload })
      return data
    },
    options,
  )
}

export const useAddFloorMutation = () => {
  return useMutation(({ id, payload }) => addFloor({ id, payload }))
}

export const useDeleteFloorMutation = () => {
  return useMutation(({ outletId, roomId }) => deleteFloor({ outletId, roomId }))
}

export const useEditFloorMutation = () => {
  return useMutation(({ outletId, roomId, body }) => editFloor({ outletId, roomId, body }))
}

export const useResetOrderTablesByTableMutation = () => {
  return useMutation(({ payload }) => resetOrderTablesByTable({ payload }))
}

export const useResetOrderTablesByRoomMutation = () => {
  return useMutation(({ payload }) => resetOrderTablesByRoom({ payload }))
}

export const usePosRoles = (options) => {
  return useQuery(
    ['pos-roles'],
    async () => {
      const {
        data: { data },
      } = await getPosRoles()
      return data
    },
    { ...options },
  )
}

export const useEditPosRole = () => {
  return useMutation(({ id, payload }) => editPosRole({ id, payload }))
}

export const usePosOjols = ({ options, payload }) => {
  return useQuery(
    ['pos-ojols', payload],
    async () => {
      const {
        data: { data },
      } = await getPosOjols({ payload })
      return data
    },
    { ...options },
  )
}
export const useAddPosOjol = () => {
  return useMutation(({ payload }) => addPosOjol({ payload }))
}

export const useEditPosOjol = () => {
  return useMutation(({ id, payload }) => editPosOjol({ id, payload }))
}

export const useDeletePosOjol = () => {
  return useMutation(({ id }) => deletePosOjol({ id }))
}

export const useInfiniteOjolSuggestion = (payload, options = {}, apiOptions = {}) => {
  return useInfiniteQuery(
    ['infinite/ojols/suggestion', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const {
          data: { data },
        } = await getSuggestionOjols({ page: pageParam, ...payload }, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useOutletDetail = ({ options, id }) => {
  return useQuery(
    ['outletDetail', id],
    async () => {
      const {
        data: { data },
      } = await getPosOutletDetail({ id })
      return data
    },
    { ...options },
  )
}

export const useInfiniteOutletSuggestion = (payload, options = {}, apiOptions = {}) => {
  return useInfiniteQuery(
    ['infinite/outlets/suggestion', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const {
          data: { data },
        } = await getSuggestionOutlet({ page: pageParam, ...payload }, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}

export const usePosShifts = ({ params, options }) => {
  return useQuery(
    ['pos-shifts', params],
    async () => {
      const {
        data: { data },
      } = await getPosShifts(params)
      return data
    },
    options,
  )
}

export const usePosShiftDetail = ({ id, options }) => {
  return useQuery(
    ['pos-shift-detail', id],
    async () => {
      const {
        data: { data },
      } = await getPosShiftDetail(id)
      return data
    },
    options,
  )
}

export const useSalesByPaymentType = ({ params, options }) => {
  return useQuery(
    ['pos-sales-by-payment-types', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesByPaymentType(params)
      return data
    },
    options,
  )
}

export const usePosOrders = (params = {}, options = {}) => {
  return useQuery(
    ['pos-orders', params],
    async () => {
      const {
        data: { data },
      } = await getPosOrders(params)
      return data
    },
    { ...options },
  )
}

export const usePosOrder = (id, options = {}) => {
  return useQuery(
    ['pos-order', id],
    async () => {
      const {
        data: { data },
      } = await getPosOrder(id)
      return data
    },
    { enabled: !!id, ...options },
  )
}

export const usePosOrderSummary = (params = {}, options = {}) => {
  return useQuery(
    ['pos-order-summary', params],
    async () => {
      const {
        data: { data },
      } = await getPosOrderSummary(params)
      return data
    },
    { ...options },
  )
}

export const usePosSalesByInvoiceTypes = ({ params, options }) => {
  return useQuery(
    ['pos-sales-by-invoice-types', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesByInvoiceTypes(params)
      return data
    },
    options,
  )
}

export const useSalesByPaymentTypeDetail = ({ params, options }) => {
  return useQuery(
    ['pos-sales-by-payment-type', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesByPaymentTypeDetail(params)
      return data
    },
    options,
  )
}

export const usePosSalesPriceRule = ({ params, options }) => {
  return useQuery(
    ['pos-sales-price-rule', params],
    async () => {
      const { data: response } = await getPosSalesPriceRule(params)
      return response?.data ?? response
    },
    options,
  )
}

export const usePosSalesPriceRuleTransactions = ({ params, options = {} }) => {
  const { priceRuleId, fingerprint } = params || {}
  const { enabled: enabledOption = true, ...restOptions } = options
  return useQuery(
    ['pos-sales-price-rule-transactions', params],
    async () => {
      const { data: response } = await getPosSalesPriceRuleTransactions(params)
      return response?.data ?? response
    },
    {
      ...restOptions,
      enabled: !!priceRuleId && !!fingerprint && enabledOption,
    },
  )
}

export const usePosSalesByInvoiceType = ({ params, options }) => {
  return useQuery(
    ['pos-sales-by-invoice-type', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesByInvoiceType(params)
      return data
    },
    options,
  )
}

export const usePosSalesDetail = ({ params, options }) => {
  return useQuery(
    ['pos-sales-detail', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesDetail(params)
      return data
    },
    options,
  )
}

export const usePosSalesDetailSummary = ({ params, options }) => {
  return useQuery(
    ['pos-sales-detail-summary', params],
    async () => {
      const {
        data: { data },
      } = await getPosSalesDetailSummary(params)
      return data
    },
    options,
  )
}

export const useCreateQRCodePayment = () => {
  return useMutation(({ apiOptions, ...payload }) => createQRCodePayment(payload, apiOptions))
}

export const useCreatePaymentGateway = () => {
  return useMutation(({ apiOptions, ...payload }) => createPaymentGateway(payload, apiOptions))
}

export const useCreatePosInvoice = () => {
  return useMutation(({ apiOptions, ...payload }) => createPosInvoice(payload, apiOptions))
}

export const usePayPosOrder = () => {
  return useMutation(({ id, apiOptions, ...payload }) => payPosOrder(id, payload, apiOptions))
}

export const useSendMailOrder = () => {
  return useMutation(({ apiOptions, ...params }) => sendEmailOrderBill(params, apiOptions))
}

export const useChangePin = () => {
  return useMutation(({ apiOptions, ...payload }) => changePin(payload, apiOptions))
}

export const useVerifyPin = () => {
  return useMutation(({ apiOptions, ...payload }) => verifyPin(payload, apiOptions))
}

export const usePosCashierCashWidget = (params = {}, options = {}, apiOptions = {}) => {
  return useQuery(
    ['posCashierCashWidget', params],
    async () => {
      const {
        data: { data },
      } = await getPosCashierWidget(params, apiOptions)
      return data
    },
    { ...options, enabled: !!params.outlet_id && !!params.pos_shift_id },
  )
}

export const useAddPosReturn = () => {
  return useMutation(({ apiOptions, ...payload }) => addPosReturn(payload, apiOptions))
}

export const usePosCashierCashInOut = (params = {}, options = {}, apiOptions = {}) => {
  return useQuery(
    ['posCashierCashInOut', params],
    async () => {
      const {
        data: { data },
      } = await getPosCashierCashInOut(params, apiOptions)
      return data
    },
    { enabled: !!params.outlet_id && !!params.pos_shift_id, ...options },
  )
}

export const useDeletePosCashInOut = () => {
  return useMutation(({ id, apiOptions, ...body }) =>
    deleteFinanceBankTransWithReason(id, body, apiOptions),
  )
}
